import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';

// ============================================
// INTERFACES / MODELS
// ============================================

export interface ColumnHeaderData {
  COL_KEY: number;
  COL_DESC: string;
  COL_ORDER: number;
  COL_PRIORITY: number;
  COL_SEC?: string;
  VALIDATION_RULE?: string;
  DROP_DOWN_ACTION?: string;
  IS_MANDATORY?: string;
  IS_PREF_MANDATORY?: string;
  MAX_LENGTH?: number;
  actionData?: DropdownOption[];
}

export interface DropdownOption {
  VALUE: string;
  LABEL: string;
}

export interface LineData {
  SCHD_KEY: number;
  LINE_KEY: number;
  LINE_NO: string;
  LINE_DESC: string;
  LINE_ORDER: number;
  LINE_SEC?: string;
  LINE_VAL?: string;
  SNO: number;
  IS_LINE_MULTI: string;
  IS_DISPLAY: string;
  COL_KEY: number;
  COL_DESC: string;
  COL_DESC_INFO?: string;
  COL_ORDER: number;
  CELL_VALUE: any;
  ADJ_KEY?: number | null;
  EDITABLE_Y_N: string;
  UI_COMP: string;
  LINE_COMP?: string;
  ACTION_Y_N: string;
  IS_COL_MULTI: string;
}

export interface GroupedFormLine {
  SCHD_KEY: number;
  LINE_KEY: number;
  LINE_NO: string;
  LINE_DESC: string;
  LINE_ORDER: number;
  LINE_SEC?: string;
  VALIDATION_RULE?: string;
  SNO: number;
  IS_LINE_MULTI: string;
  IS_DISPLAY: string;
  IS_MANDATORY?: string;
  IS_PREF_MANDATORY?: string;
  PREF_MAN_COLS?: string[];
  VALIDATION_MSG?: string;
  ERR_MSG?: string;
  items: FormLineItem[];
}

export interface FormLineItem {
  SCHD_KEY: number;
  LINE_KEY: number;
  COL_KEY: number;
  COL_DESC: string;
  COL_DESC_INFO?: string;
  COL_ORDER: number;
  IS_ENABLED: string;
  SNO: number;
  ADJ_KEY?: number | null;
  IS_COL_MULTI: string;
  UI_COMP: string;
  LINE_COMP?: string;
  ACTION_Y_N: string;
  IS_MANDATORY: string;
  IS_PREF_MANDATORY?: string;
  MAX_LENGTH?: number;
  CELL_VALUE: any;
  OPERATION_TYPE?: string;
  IS_ERROR?: string;
  actionData?: DropdownOption[];
}

export interface SaveDataItem {
  SCHD_KEY: number;
  LINE_KEY: number;
  COL_KEY: number;
  ADJ_KEY?: number | null;
  SNO: number;
  CELL_VALUE?: any;
  OPERATION_TYPE: string | null;
}

export interface ClobSettingsObj {
  jcd_key: number | string;
  p_form_schd: string;
  p_code_group: string;
}

export interface LockStatus {
  LOCK_STATUS: string;
}

export interface FilterParams {
  tax_year?: string;
  scenario?: string;
  p_form?: string;
  p_schedule?: string;
  p_filer_key?: string | number;  // Can be single number or comma-separated string
  p_filer_partner_key?: string | number | null;  // Can be single number or comma-separated string
  p_filer_label?: string;  // Label for entity name (for confirmation modal)
  p_filer_partner_label?: string;  // Label for partner name (for confirmation modal)
  jcd_key?: number;
  [key: string]: any;
}

// ============================================
// SERVICE
// ============================================

@Injectable({
  providedIn: 'root'
})
export class F1065K2DataEntryService {

  constructor(
    private http: HttpClient,
    private gtwSnackbarService: GtwSnackbarService
  ) { }

  // ============================================
  // API CALLS - Using HTTP GET with query params
  // (matching AngularJS JsonObjectFactory.getJSONObj pattern)
  // ============================================

  /**
   * Get column header details for the form
   * Action code: ea2llc
   */
  getColumnHeaders(
    baseUrl: string,
    taxYear: string,
    codeGroup: string,
    globalParams: any = {},
    filterParams: FilterParams = {}
  ): Observable<ColumnHeaderData[]> {
    // Build params like AngularJS: merge filterParams -> explicit params -> globalParams
    const explicitParams = {
      action_code: 'ea2llc',
      tax_year: taxYear,
      code_group: codeGroup
    };

    return this.getRequest<{ jsonObject: ColumnHeaderData[] }>(
      `${baseUrl}/loadJsonObject`,
      explicitParams,
      globalParams,
      filterParams
    ).pipe(
      map(response => response.jsonObject || [])
    );
  }

  /**
   * Get dropdown data for a column
   */
  getDropdownData(
    baseUrl: string,
    actionCode: string,
    codeGroup: string,
    taxYear: string,
    globalParams: any = {},
    filterParams: FilterParams = {}
  ): Observable<DropdownOption[]> {
    const explicitParams: any = {
      action_code: actionCode,
      p_tax_year: taxYear
    };

    // Only add p_code_group if it has a value (matching original behavior)
    if (codeGroup) {
      explicitParams.p_code_group = codeGroup;
    }

    return this.getRequest<{ jsonObject: DropdownOption[] }>(
      `${baseUrl}/loadJsonObject`,
      explicitParams,
      globalParams,
      filterParams
    ).pipe(
      map(response => response.jsonObject || [])
    );
  }

  /**
   * Get line details for the form
   * Action codes vary by component (bwlb9h, w5bepp, etc.)
   */
  getLineDetails(
    baseUrl: string,
    actionCode: string,
    taxYear: string,
    filerKey: string | number,
    codeGroup: string,
    globalParams: any = {},
    filterParams: FilterParams = {},
    filerPartnerKey?: string | number | null
  ): Observable<LineData[]> {
    const explicitParams: any = {
      action_code: actionCode,
      p_tax_year: taxYear,
      p_filer_key: filerKey,
      code_group: codeGroup
    };

    if (filerPartnerKey !== undefined && filerPartnerKey !== null) {
      explicitParams.p_filer_partner_key = filerPartnerKey;
    }

    return this.getRequest<{ jsonObject: LineData[] }>(
      `${baseUrl}/loadJsonObject`,
      explicitParams,
      globalParams,
      filterParams
    ).pipe(
      map(response => response.jsonObject || [])
    );
  }

  /**
   * Get lock status
   * Action code: n10cpv
   */
  getLockStatus(
    baseUrl: string,
    scenario: string,
    jcdKey: number,
    globalParams: any = {},
    filterParams: FilterParams = {}
  ): Observable<LockStatus[]> {
    const explicitParams = {
      action_code: 'n10cpv',
      p_scenario: scenario,
      p_jcd_key: jcdKey
    };

    return this.getRequest<{ jsonObject: LockStatus[] }>(
      `${baseUrl}/loadJsonObject`,
      explicitParams,
      globalParams,
      filterParams
    ).pipe(
      map(response => response.jsonObject || [])
    );
  }

  /**
   * Save form data (uses POST)
   * Action code: bixi9s
   */
  saveData(
    baseUrl: string,
    clobSettings: ClobSettingsObj,
    clobData: SaveDataItem[],
    globalParams: any = {},
    filterParams: FilterParams = {},
    taxYearInputs: any[] = []
  ): Observable<any> {
    const jsonSettings = JSON.stringify(clobSettings);
    const jsonObj = JSON.stringify(clobData);

    // Compute isIssueIDEnabled (matching GlobalService.isIssueIDEnabled)
    const issueIdParams = this.computeIssueIdEnabled(
      filterParams.tax_year, 
      filterParams.scenario, 
      taxYearInputs
    );

    // Compute checkLockedbj (matching GlobalService.modalLockFilters)
    const checkLockedbj = this.computeCheckLockedbj(filterParams.p_filer_key);

    // Merge parameters like AngularJS FTC1065K2DataEntryFactory.saveData:
    // Original: params = _.merge({}, GlobalService.globalParams, params);
    // globalParams FIRST (lower precedence), filterParams LAST (higher precedence)
    let params = _.merge({}, globalParams, filterParams);
    
    // Remove labels from params - they are for display only, not for API
    delete params.p_filer_label;
    delete params.p_filer_partner_label;
    
    // Add checkLockedbj to params (like modalLockFilters does) - always include it
    params.checkLockedbj = checkLockedbj;
    
    params = _.extend({ clob_data: jsonObj }, params);
    params = _.extend({ clob_settings: jsonSettings }, params);
    params = _.merge({}, issueIdParams, params);

    console.log('[F1065K2-Service] saveData params:', params);

    return this.postRequest<any>(
      `${baseUrl}/saveJsonObject?action_code=bixi9s`,
      params
    );
  }

  /**
   * Compute scenarioCode and is_issue_key_enabled
   * Matches GlobalService.isIssueIDEnabled logic
   */
  private computeIssueIdEnabled(taxYear: string | undefined, scenario: string | undefined, taxYearInputs: any[]): any {
    const result: any = {};
    
    if (!taxYear || !scenario || !taxYearInputs || taxYearInputs.length === 0) {
      return result;
    }

    // Find the tax year entry
    const taxYearEntry = taxYearInputs.find((i: any) => i.value == taxYear);
    if (!taxYearEntry || !taxYearEntry.scenarios) {
      return result;
    }

    // Find the scenario within the tax year
    const foundScenario = taxYearEntry.scenarios.find((s: any) => s.scenario == scenario);
    if (foundScenario && foundScenario.scenario_type_code) {
      result.scenarioCode = foundScenario.scenario_type_code;
      result.is_issue_key_enabled = foundScenario.is_issue_key_enabled;
    }

    return result;
  }

  /**
   * Compute checkLockedbj from filer key
   * Matches GlobalService.modalLockFilters logic
   * Always include checkLockedbj, even when filerKey is -1
   */
  private computeCheckLockedbj(filerKey: string | number | undefined): string {
    // Use filerKey or default to -1 if not provided
    const key = filerKey !== undefined && filerKey !== null ? filerKey : -1;
    const checkLockObj = [{ combination_key: Number(key) }];
    return JSON.stringify(checkLockObj);
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Generic GET request helper - matching AngularJS $http.get(_url, {params: params})
   * Merges params in order: filterParams -> explicitParams -> globalParams
   */
  private getRequest<T>(
    url: string, 
    explicitParams: any, 
    globalParams: any = {}, 
    filterParams: any = {}
  ): Observable<T> {
    // Merge order matching JsonObjectFactory.getJSONObj:
    // Start with globalParams, then add filterParams (which overrides), then explicitParams (which overrides)
    const mergedParams: { [key: string]: any } = {};
    
    // Add globalParams
    for (const key in globalParams) {
      if (globalParams.hasOwnProperty(key)) {
        mergedParams[key] = globalParams[key];
      }
    }
    
    // Add filterParams (overrides globalParams)
    for (const key in filterParams) {
      if (filterParams.hasOwnProperty(key)) {
        mergedParams[key] = filterParams[key];
      }
    }
    
    // Add explicitParams (overrides everything)
    for (const key in explicitParams) {
      if (explicitParams.hasOwnProperty(key)) {
        mergedParams[key] = explicitParams[key];
      }
    }
    
    // Remove any circular reference keys
    delete mergedParams.checkLockedbj;

    // Build HttpParams from merged object
    let httpParams = new HttpParams();
    for (const key in mergedParams) {
      if (mergedParams.hasOwnProperty(key)) {
        const value = mergedParams[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, String(value));
        }
      }
    }

    return this.http.get<T>(url, { 
      params: httpParams, 
      withCredentials: true 
    }).pipe(
      catchError(error => {
        console.error('HTTP GET Error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Generic POST request helper (for save operations)
   */
  private postRequest<T>(url: string, params: any): Observable<T> {
    const body = this.buildUrlEncodedBody(params);

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
      }),
      withCredentials: true
    };

    return this.http.post<T>(url, body, httpOptions).pipe(
      catchError(error => {
        console.error('HTTP POST Error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Build URL-encoded body from params object (for POST)
   */
  private buildUrlEncodedBody(params: any): string {
    const urlEncoded = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, String(params[key]));
      }
    });

    return urlEncoded.toString();
  }

  // ============================================
  // SNACKBAR METHODS
  // ============================================

  openSnackBar(message: string, panelClass: string = 'info-snackbar'): void {
    this.gtwSnackbarService.openSnackBar(message, panelClass as any, true);
  }

  showSuccess(message: string): void {
    this.openSnackBar(message, 'success-snackbar');
  }

  showError(message: string): void {
    this.openSnackBar(message, 'error-snackbar');
  }

  showWarning(message: string): void {
    this.openSnackBar(message, 'warning-snackbar');
  }

  showInfo(message: string): void {
    this.openSnackBar(message, 'info-snackbar');
  }
}
